import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import dayjs from 'dayjs';
import nookies from 'nookies';
import { Button } from 'react-bootstrap';
import Discount2Up from 'components/Discount2Up';
import DiscountNavigation from 'components/DiscountNavigation';
import DiscountRowSection from 'components/DiscountRowSection';
import PageMeta from 'components/PageMeta';
import getContentData from 'data/contentstack/get-content-data';
import { getDiscountTypeMapByStore } from 'data/product-discounts';
import universalHydration from 'data/universal-hydration';
import useGetDiscountTypeMap from 'hooks/useGetDiscountTypeMap';
import sspMiddleware from 'middleware/gssp/gssp-middleware';
import {
  MODAL_SELECT_STORE,
  setCurrentModal,
} from 'stores/reducers/store-selection-modal';
import setCaching from 'utils/cache-control';
import codify from 'utils/codify';
import getCanonicalUrl from 'utils/get-canonical-url';
import getCorrelationId from 'utils/get-correlation-id';
import { sendPageView, setMetricsData } from 'utils/metrics';
import styles from './Specials.module.scss';

export default function SpecialsPage({
  bannerContent,
  canonical,
  discountSections,
  groupedDiscountsContent,
  location,
  menu,
  navigationGroups,
  noDiscountsAvailableContent,
  noStoreSelectedContent,
  productDiscountMap,
  seo,
}) {
  const dispatch = useDispatch();
  const storeSelection = useSelector((state) => state.storeSelection);
  const discountTypeMap = useGetDiscountTypeMap({
    propTypeMap: productDiscountMap,
    store: location,
  });

  const [displaySections, setDisplaySections] = useState([]);
  const [showDiscounts, setShowDiscounts] = useState(true);

  useEffect(() => {
    if (discountTypeMap) {
      const bundleDiscounts = discountTypeMap?.bundle?.discounts?.filter(
        (discount) => discount.isActive
      );
      const onSaleDiscounts = discountTypeMap?.sale?.discounts?.filter(
        (discount) => discount.isActive
      );
      const mixAndMatchDiscounts =
        discountTypeMap?.mixandmatch?.discounts?.filter(
          (discount) => discount.isActive
        );

      const filteredMixAndMatchDiscounts = groupedDiscountsContent
        ?.filter((discount) =>
          mixAndMatchDiscounts?.some(
            (d) => d.groupKey === discount.discount_id?.toLowerCase()
          )
        )
        /* eslint-disable camelcase */
        .map(({ discount_id, online_name, image, slug }) => {
          return {
            id: discount_id?.toLowerCase() ?? '',
            onlineName: online_name,
            onlineNameOverride: '',
            image,
            slug,
          };
        });
      // eslint-enable camelcase

      const today = dayjs().get('day');
      const dailyDealDiscounts = discountTypeMap?.daily?.discounts.filter(
        (discount) => discount.daySchedule[today]
      );

      const dMap = {
        bundle: bundleDiscounts,
        // cleanup buymoresave more after July2024
        buymoresavemore: filteredMixAndMatchDiscounts,
        mixandmatch: filteredMixAndMatchDiscounts,
        daily: dailyDealDiscounts,
        sale: onSaleDiscounts,
      };

      const dArray = discountSections?.map((section) => {
        const sectionType = codify(section.type);
        const discounts = dMap[sectionType] ?? [];
        return { content: section, discounts };
      });
      setDisplaySections(dArray);
      setShowDiscounts(dArray?.some((section) => section.discounts.length > 0));
    }
  }, [discountSections, discountTypeMap, groupedDiscountsContent]);

  const {
    meta_title: title,
    meta_description: description,
    keywords,
  } = seo || {};

  const { header: noDiscountsHeader, subheader: noDiscountsSubheader } =
    noDiscountsAvailableContent || {};

  const { header: noStoreHeader, button_text: noStoreButtonText } =
    noStoreSelectedContent || {};

  const { header: bannerHeader, subheader: bannerSubheader } =
    bannerContent || {};

  useEffect(() => {
    setMetricsData({
      location,
      menu,
      pageType: 'specials_page',
      title,
    });
    sendPageView(title);
  }, [location, menu, title]);

  return (
    <>
      <PageMeta
        canonical={canonical}
        description={description}
        keywords={keywords}
        title={title}
      />
      <div className="d-flex flex-column">
        <section className=" w-100 align-items-center bg-primary">
          <div
            className={classNames(
              'd-flex flex-column align-items-center justify-content-center my-25 px-8 text-cream',
              styles.banner_text
            )}
          >
            {bannerHeader && <h1>{bannerHeader}</h1>}
            {bannerSubheader && <p className="my-0">{bannerSubheader}</p>}
          </div>
        </section>
        {!storeSelection && (
          <section className="d-flex flex-column align-items-center justify-content-center text-primary my-15">
            {noStoreHeader && <h2 className="mb-8">{noStoreHeader}</h2>}
            {noStoreButtonText && (
              <Button
                data-value="select-store-link"
                variant="seconday"
                type="button"
                className="font-sofia fw-700 bg-secondary text-primary text-uppercase"
                onClick={() => dispatch(setCurrentModal(MODAL_SELECT_STORE))}
              >
                {noStoreButtonText}
              </Button>
            )}
          </section>
        )}
        {storeSelection && !showDiscounts && (
          <section className="d-flex flex-column align-items-center justify-content-center text-primary my-15">
            {noDiscountsHeader && <h2 className="mb-8">{noDiscountsHeader}</h2>}
            {noDiscountsSubheader && (
              <p className="my-0">{noDiscountsSubheader}</p>
            )}
          </section>
        )}
        {storeSelection && showDiscounts && navigationGroups?.length > 0 && (
          <DiscountNavigation navigationGroups={navigationGroups} />
        )}
        {storeSelection &&
          showDiscounts &&
          displaySections?.map((section) => {
            const { content, discounts } = section;

            if (discounts?.length === 0) {
              return;
            }

            const sectionType = content.type.toLowerCase();
            if (
              sectionType === 'bundle' ||
              sectionType === 'buy more save more' ||
              sectionType === 'sale'
            ) {
              return (
                <DiscountRowSection
                  key={sectionType}
                  discounts={discounts}
                  sectionContent={content}
                />
              );
            }
            if (sectionType === 'daily') {
              return (
                <Discount2Up
                  key={sectionType}
                  discounts={discounts}
                  sectionContent={content}
                />
              );
            }
            return null;
          })}
      </div>
    </>
  );
}

export const getServerSideProps = sspMiddleware((store) => async (ctx) => {
  setCaching('short', ctx.res);

  const { log } = ctx.req;
  const correlationId = getCorrelationId({ request: ctx.req });

  const { store: locationFromState, menu = 'recreational' } = nookies.get(ctx);

  const location = locationFromState ?? '';

  const [universal, pageContent, groupContent, discountTypeMap] =
    await Promise.allSettled([
      universalHydration({ ctx, store, correlationId, log }),
      getContentData({
        id: 'specials_page_content',
        correlationId,
        log,
        params: {
          include_branch: true,
          include: ['banner'],
        },
      }),
      getContentData({
        id: 'grouped_product_discounts',
        correlationId,
        log,
        params: {
          group_id: 'tiered-flower',
        },
      }),
      getDiscountTypeMapByStore({ correlationId, log, store: location }),
    ]);

  const props = {
    canonical: getCanonicalUrl('/specials'),
    location,
    menu,
  };

  if (universal.status === 'fulfilled') {
    props.footer = universal.value.footer;
    props.header = universal.value.header;
  }

  if (pageContent.status === 'fulfilled') {
    const pageContentData = pageContent.value?.data?.[0] ?? null;
    const seo = pageContentData?.seo;
    if (seo) {
      props.seo = seo;
    }
    const banner = pageContentData?.banner;
    if (banner) {
      props.bannerContent = banner;
    }

    const navigation = pageContentData?.navigation;
    if (navigation) {
      props.navigationGroups = navigation;
    }

    const sections = pageContentData?.sections;
    if (sections) {
      props.discountSections = sections;
    }

    const noDiscountsAvailableContent =
      pageContentData?.no_discounts_available_content;
    if (noDiscountsAvailableContent) {
      props.noDiscountsAvailableContent = noDiscountsAvailableContent;
    }

    const noStoreSelectedContent = pageContentData?.no_store_selected_content;
    if (noStoreSelectedContent) {
      props.noStoreSelectedContent = noStoreSelectedContent;
    }
  }

  if (groupContent.status === 'fulfilled') {
    props.groupedDiscountsContent =
      groupContent.value?.data?.[0]?.discounts ?? [];
  }

  if (discountTypeMap.status === 'fulfilled') {
    props.productDiscountMap = discountTypeMap.value ?? {};
  }

  props.metrics = {
    title: props.seo?.meta_title ?? 'Specials',
    pageType: 'landing_page',
    location,
    menu,
  };

  return {
    props,
  };
});
